import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { API } from "@/lib/api";

function getSessionId() {
  let id = localStorage.getItem("vg_chat_session");
  if (!id) {
    id = (crypto.randomUUID && crypto.randomUUID()) || `s_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("vg_chat_session", id);
  }
  return id;
}

export const ChatAssistant = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, open]);

  // restore prior conversation once when first opened
  useEffect(() => {
    if (!open || messages.length > 0) return;
    (async () => {
      try {
        const res = await fetch(`${API}/chat/history/${sessionId.current}`);
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && data.length) setMessages(data);
        }
      } catch {
        /* ignore restore errors */
      }
    })();
  }, [open, messages.length]);

  const send = useCallback(
    async (preset) => {
      const text = (preset ?? input).trim();
      if (!text || streaming) return;
      setInput("");
      setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
      setStreaming(true);
      try {
        const token = localStorage.getItem("vg_token");
        const res = await fetch(`${API}/chat/stream`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ session_id: sessionId.current, message: text }),
        });
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split("\n\n");
          buffer = parts.pop();
          for (const part of parts) {
            const line = part.trim();
            if (!line.startsWith("data:")) continue;
            let payload;
            try {
              payload = JSON.parse(line.slice(5).trim());
            } catch {
              continue;
            }
            if (payload.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  role: "assistant",
                  content: copy[copy.length - 1].content + payload.delta,
                };
                return copy;
              });
            } else if (payload.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: t("chat.error") };
                return copy;
              });
            }
          }
        }
      } catch {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: t("chat.error") };
          return copy;
        });
      } finally {
        setStreaming(false);
      }
    },
    [input, streaming, t]
  );

  const suggestions = [t("chat.sug1"), t("chat.sug2"), t("chat.sug3")];

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-5 right-5 z-50 h-14 w-14 rounded-full grid place-items-center bg-gradient-to-br from-violet-600 via-indigo-600 to-cyan-500 shadow-lg shadow-violet-900/40 hover:scale-105 active:scale-95 transition-transform"
        data-testid="chat-launcher-button"
        aria-label={t("chat.title")}
      >
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <X size={22} className="text-white" />
            </motion.span>
          ) : (
            <motion.span key="chat" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
              <MessageCircle size={24} className="text-white" />
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 300, damping: 26 }}
            className="fixed bottom-24 right-5 z-50 w-[calc(100vw-2.5rem)] sm:w-[380px] h-[70vh] sm:h-[540px] flex flex-col rounded-3xl overflow-hidden border border-white/10 bg-[#0C0E15]/95 backdrop-blur-xl shadow-2xl shadow-black/50"
            data-testid="chat-panel"
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3.5 border-b border-white/10 bg-gradient-to-r from-violet-600/20 to-cyan-500/10">
              <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-violet-600 to-cyan-500 grid place-items-center shrink-0">
                <Sparkles size={17} className="text-white" />
              </div>
              <div className="min-w-0">
                <h4 className="font-head font-semibold text-white text-sm leading-tight">{t("chat.title")}</h4>
                <p className="text-[11px] text-slate-400 leading-tight">{t("chat.subtitle")}</p>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
              {messages.length === 0 && (
                <div className="space-y-4">
                  <div className="flex gap-2.5">
                    <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-violet-600 to-cyan-500 grid place-items-center shrink-0">
                      <Sparkles size={15} className="text-white" />
                    </div>
                    <div className="rounded-2xl rounded-tl-sm bg-white/5 border border-white/10 px-3.5 py-2.5 text-sm text-slate-200 leading-relaxed">
                      {t("chat.greeting")}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 pl-10">
                    {suggestions.map((s) => (
                      <button
                        key={s}
                        onClick={() => send(s)}
                        className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-slate-300 hover:border-violet-500/50 hover:text-white transition-colors"
                        data-testid="chat-suggestion"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((m, i) => (
                <div key={i} className={`flex gap-2.5 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                  {m.role === "assistant" && (
                    <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-violet-600 to-cyan-500 grid place-items-center shrink-0">
                      <Sparkles size={15} className="text-white" />
                    </div>
                  )}
                  <div
                    className={`rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap break-words max-w-[78%] ${
                      m.role === "user"
                        ? "rounded-tr-sm bg-gradient-to-br from-violet-600 to-indigo-600 text-white"
                        : "rounded-tl-sm bg-white/5 border border-white/10 text-slate-200"
                    }`}
                  >
                    {m.content || (streaming && i === messages.length - 1 ? <Loader2 size={15} className="vg-spin text-slate-400" /> : "")}
                  </div>
                </div>
              ))}
            </div>

            {/* Input */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="p-3 border-t border-white/10 flex items-center gap-2"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t("chat.placeholder")}
                className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm text-white placeholder:text-slate-500 outline-none focus:border-violet-500/50 transition-colors"
                data-testid="chat-input"
              />
              <button
                type="submit"
                disabled={streaming || !input.trim()}
                className="h-10 w-10 shrink-0 rounded-xl grid place-items-center bg-gradient-to-br from-violet-600 to-cyan-500 text-white disabled:opacity-50 hover:opacity-90 transition-opacity"
                data-testid="chat-send-button"
              >
                {streaming ? <Loader2 size={17} className="vg-spin" /> : <Send size={17} />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
